import { List, Body1 } from '@tumelo/designsystem'
import { Formik, Form } from 'formik'
import React from 'react'
import styled, { useTheme } from 'styled-components'
import * as yup from 'yup'
import { SubmitButton, Field } from '@/tumeloshared'
import { AuthHeader } from '../Auth/Components/AuthHeader'
import { AuthSplitContainer } from '../Auth/Components/AuthSplitContainer'
import { ErrorContainer } from '../Auth/Components/ErrorContainer'

interface Props {
  submit: (details: ConfirmCodeValues) => Promise<void>
  resendCode: (email: string) => Promise<void>
  email: string
  error: string | undefined
}

const signUpSchema = yup.object({
  email: yup.string().required('Required'),
  code: yup.string().required('Code required'),
})

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface ConfirmCodeValues extends yup.Asserts<typeof signUpSchema> {}

export const ConfirmCodeForm: React.FC<Props> = ({ submit, email, resendCode, error }) => {
  const [requestedResend, setRequestedResend] = React.useState<boolean | 'submitting'>(false)
  const initialValues: ConfirmCodeValues = {
    email,
    code: '',
  }

  const onSubmit = async (values: ConfirmCodeValues) => {
    const code = values.code.trim()
    await submit({ email: values.email, code })
  }

  const onResend = async () => {
    setRequestedResend(true)
    await resendCode(email)
  }

  const { colors } = useTheme()

  const thingsToCheck = [
    'Please check you&apos;ve given us the correct email address.',
    'Check your spam/junk box.',
    'If you request another code, make sure to use the latest one. The others will now be invalid.',
  ]
  return (
    <AuthSplitContainer>
      <ErrorContainer error={error} />
      <AuthHeader
        subHeading="We have sent you a confirmation code to the following email address. Please copy and paste that code into the space below:"
        heading="Confirm your email address"
      />
      <Formik
        initialValues={initialValues}
        onSubmit={async (values) => onSubmit(values)}
        validationSchema={signUpSchema}
      >
        {({ values }) => (
          <Form>
            <Field
              name="email"
              label="Email address"
              required
              type="email"
              autoComplete="email"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck="false"
              disabled={email !== ''}
            />
            <Field
              name="code"
              label="Confirmation Code"
              required
              type="text"
              autoComplete="one-time-code"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck="false"
            />
            <BottomContainer>
              <SubmitButtonContainer>
                <SubmitButton label="Confirm Account" />
              </SubmitButtonContainer>
              <Body1>Having trouble with your code?</Body1>
              <Body1>It can take up to 20 minutes to arrive so please hang on. In the meantime:</Body1>
              <List listArray={thingsToCheck} />
              {requestedResend === false ? (
                <Body1>
                  Still no joy?{' '}
                  <InlineButton role="button" onClick={() => onResend()} onKeyDown={() => onResend()} tabIndex={0}>
                    Resend Code.
                  </InlineButton>{' '}
                </Body1>
              ) : (
                <Body1 color={colors.success.dark}>Sent! The code is on its way to {values.email}</Body1>
              )}
            </BottomContainer>
          </Form>
        )}
      </Formik>
    </AuthSplitContainer>
  )
}

const SubmitButtonContainer = styled.div`
  display: flex;
  justify-content: end;
  padding: 0 1rem 1rem 0;
`

const BottomContainer = styled.div`
  margin-top: 2rem;
`

const InlineButton = styled.span`
  cursor: pointer;
  color: ${({ theme }) => theme.colors.secondary.light};
  &:hover {
    text-decoration: underline;
  }
`
